import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ChevronDownIcon } from 'lucide-react';
import { newsItems } from '../data/newsData';
const ITEMS_PER_PAGE = 6;
export function NewsPage() {
  const {
    t,
    setLocale,
    direction,
    locale
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useScrollReveal();
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  const filters = [{
    id: 'all',
    label: t('newsPage.filters.all')
  }, {
    id: 'partnerships',
    label: t('newsPage.filters.partnerships')
  }, {
    id: 'product-launches',
    label: t('newsPage.filters.productLaunches')
  }, {
    id: 'milestones',
    label: t('newsPage.filters.milestones')
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const selectedFilter = filters.find(f => f.id === filter);
  const filteredNews = newsItems.filter(item => filter === 'all' || item.category === filter);
  const totalPages = Math.ceil(filteredNews.length / ITEMS_PER_PAGE);
  const displayedNews = filteredNews.slice(0, currentPage * ITEMS_PER_PAGE);
  const hasMore = currentPage < totalPages;
  const handleLoadMore = () => {
    setCurrentPage(prev => prev + 1);
  };
  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-917">
      {/* SEO Pagination Meta */}
      {currentPage > 1 && <link rel="prev" href={`${location.pathname}?page=${currentPage - 1}`} data-id="element-918" />}
      {hasMore && <link rel="next" href={`${location.pathname}?page=${currentPage + 1}`} data-id="element-919" />}

      <Header variant="dark" data-id="element-920" />

      <main data-id="element-921">
        {/* White header background spacer */}
        <div className="bg-white h-20" data-id="element-922" />

        {/* 1. Hero Banner - Matching ContactPage style */}
        <section className="gradient-dark-horizontal py-16 md:py-20 relative overflow-hidden" ref={heroRef} aria-label="News Hero" data-id="element-923">
          {/* Top-left decorative SVG */}
          <img src="https://cdn.magicpatterns.com/uploads/oSPxnKHLzspYqMxuH2SAJd/inner-top-left.svg" alt="" role="presentation" aria-hidden="true" className="absolute top-0 left-0 w-[200px] md:w-[300px] h-auto pointer-events-none opacity-20" data-id="element-924" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-925">
            <h1 className={`text-white font-bold leading-[1.1] mb-4 uppercase text-[32px] md:text-[44px] lg:text-[50px] reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-926">
              {t('newsPage.hero.title')}
            </h1>
            <p className={`text-white/80 text-base md:text-lg whitespace-pre-line leading-relaxed max-w-lg reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-927">
              {t('newsPage.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* 2. News Grid Section */}
        <section className="py-16 md:py-24 bg-white relative" ref={gridRef} aria-label="News Articles" data-id="element-928">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-929">
            {/* Filters */}
            <div className="flex justify-end mb-12" data-id="element-930">
              <div className="flex items-center gap-6" data-id="element-931">
                <span className="text-gray-400 font-medium text-sm" data-id="element-932">
                  {t('newsPage.filterBy')}
                </span>

                {/* Custom Dropdown */}
                <div className="relative" ref={dropdownRef} data-id="element-933">
                  <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center gap-2 text-[#061D76] font-semibold text-sm focus:outline-none" data-id="element-934">
                    {selectedFilter?.label}
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} data-id="element-935" />
                  </button>

                  {isDropdownOpen && <div className="absolute top-full right-0 mt-2 bg-white shadow-lg border border-gray-100 min-w-[180px] z-50" data-id="element-936">
                      {filters.map(f => <button key={f.id} onClick={() => {
                    setFilter(f.id);
                    setIsDropdownOpen(false);
                  }} className={`block w-full text-right px-4 py-3 text-sm transition-colors ${filter === f.id ? 'bg-[#061D76] text-white font-semibold' : 'text-[#061D76] hover:bg-gray-50'}`} data-id="element-937">
                          {f.label}
                        </button>)}
                    </div>}
                </div>
              </div>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-938">
              {displayedNews.map((item, index) => <Link key={item.id} to={locale === 'ar' ? `/ar/news/${item.slug}` : `/news/${item.slug}`} className={`group cursor-pointer transition-shadow duration-300 reveal-hidden ${gridVisible ? 'animate-fade-up' : ''}`} style={{
              animationDelay: `${index * 100}ms`
            }} data-id="element-939">
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100" data-id="element-940">
                    <img src={item.image} alt={locale === 'ar' ? item.title_ar : item.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" data-id="element-941" />
                  </div>

                  {/* Content */}
                  <div className="bg-[#F5F8F8] p-6 flex flex-col min-h-[220px]" data-id="element-942">
                    <span className="text-[#061D76] font-medium text-sm mb-6 block" data-id="element-943">
                      {locale === 'ar' ? item.date_ar : item.date}
                    </span>

                    <h3 className="text-[#061D76] font-bold text-xl leading-snug mb-auto line-clamp-3" data-id="element-944">
                      {locale === 'ar' ? item.title_ar : item.title}
                    </h3>

                    <div className="mt-6 flex justify-end" data-id="element-945">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={`transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform ${direction === 'rtl' ? 'scale-x-[-1]' : ''}`} data-id="element-946">
                        <path d="M6 16H26M26 16L18 8M26 16L18 24" stroke="#061D76" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-947" />
                      </svg>
                    </div>
                  </div>
                </Link>)}
            </div>

            {/* Load More */}
            {hasMore && <nav aria-label="Pagination" className="flex justify-center" data-id="element-948">
                <button onClick={handleLoadMore} className="btn-primary" aria-label={`${t('newsPage.loadMore')} - ${t('newsPage.hero.title')} ${locale === 'ar' ? `صفحة ${currentPage + 1} من ${totalPages}` : `Page ${currentPage + 1} of ${totalPages}`}`} data-id="element-949">
                  {t('newsPage.loadMore')}
                </button>
              </nav>}

            {/* SEO: Hidden pagination links for crawlers */}
            {totalPages > 1 && <div className="sr-only" role="navigation" aria-label="Page navigation" data-id="element-950">
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <a key={page} href={`${location.pathname}?page=${page}`} aria-label={`Page ${page}`} aria-current={page === currentPage ? 'page' : undefined} data-id="element-951">
                    Page {page}
                  </a>)}
              </div>}
          </div>
        </section>
      </main>

      <Footer data-id="element-952" />
    </div>;
}